import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Users, UserCheck, Calendar, Wallet, TrendingUp, TrendingDown,
  AlertTriangle, Mail, CreditCard, Clock, Headphones, Star,
  ChevronRight, RefreshCw, Car, CheckCircle, XCircle, Eye
} from 'lucide-react';

/* Design System Status Colors */
const STATUS_COLORS = {
  PENDING: 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20',
  ACCEPTED: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20',
  IN_PROGRESS: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20',
  AWAITING_CODE: 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20',
  COMPLETED: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20',
  PAID: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20',
  CANCELLED: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20',
  REJECTED: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20',
  ATIVO: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20',
  INATIVO: 'bg-slate-100 text-slate-600 border border-slate-200',
  SUSPENSO: 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20',
  BLOQUEADO: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20',
  open: 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20',
  pending: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20',
  resolved: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20',
  closed: 'bg-slate-100 text-slate-600 border border-slate-200',
  completed: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20'
};

const ALERT_ICONS = {
  mail: Mail,
  'credit-card': CreditCard,
  headphones: Headphones,
  'alert-triangle': AlertTriangle,
  clock: Clock
};

const ALERT_COLORS = {
  danger: 'bg-[#EF4444]/5 border-[#EF4444]/20 text-[#EF4444]',
  warning: 'bg-[#F97316]/5 border-[#F97316]/20 text-[#F97316]',
  info: 'bg-[#2563EB]/5 border-[#2563EB]/20 text-[#2563EB]'
};

// PROMPT BLINDADO: o backend envia alert.action com prefixo legado `/admin/...`.
// As rotas reais do app admin vivem sob `/control-portal-clube/...`.
// Mapeamento purely-frontend (sem tocar no backend).
const ALERT_ACTION_MAP = {
  '/admin/clients': '/control-portal-clube/clients',
  '/admin/financial': '/control-portal-clube/financial',
  '/admin/support': '/control-portal-clube/support',
  '/admin/fraud': '/control-portal-clube/fraud',
  '/admin/subscriptions': '/control-portal-clube/subscriptions',
  '/admin/providers': '/control-portal-clube/providers',
  '/admin/appointments': '/control-portal-clube/appointments',
  '/admin/reviews': '/control-portal-clube/reviews',
};

const resolveAdminPath = (action) => {
  if (!action || typeof action !== 'string') return action;
  if (action.startsWith('/control-portal-clube')) return action;
  // Tentar match do prefixo mais específico para o mais genérico
  for (const legacy of Object.keys(ALERT_ACTION_MAP)) {
    if (action === legacy || action.startsWith(`${legacy}/`) || action.startsWith(`${legacy}?`)) {
      return action.replace(legacy, ALERT_ACTION_MAP[legacy]);
    }
  }
  return action;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchDashboard();
  }, [user, navigate]);

  const fetchDashboard = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/dashboard-v2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error(error);
      // Fallback para dashboard antigo
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboard({
          kpis: {
            clients: { total: res.data.metrics?.total_clients || 0, active: res.data.metrics?.total_clients || 0, new_this_week: res.data.metrics?.recent_clients_7d || 0 },
            providers: { total: res.data.metrics?.total_providers || 0, active: res.data.metrics?.total_providers || 0, new_this_week: res.data.metrics?.recent_providers_7d || 0 },
            appointments: { total: res.data.metrics?.total_appointments || 0, today: 0 },
            revenue: { monthly: 0, last_month: 0, growth_percent: 0 },
            reviews: { average_rating: 0, total: 0 }
          },
          alerts: [],
          appointments_by_status: res.data.appointments_by_status || {},
          recent: { clients: [], providers: [], appointments: [], reviews: [], transfers: [] }
        });
      } catch (e) {
        toast.error('Erro ao carregar dashboard');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard" subtitle="Carregando dados...">
        <div className="space-y-6">
          {/* KPI Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </Card>
            ))}
          </div>
          {/* Tables Skeletons */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-6 w-40 mb-4" />
                <div className="space-y-3">
                  {[...Array(4)].map((_, j) => (
                    <Skeleton key={j} className="h-12 w-full" />
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const kpis = dashboard?.kpis || {};
  const alerts = dashboard?.alerts || [];
  const appointmentStatus = dashboard?.appointments_by_status || {};
  const recent = dashboard?.recent || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0B1F3A]">Dashboard</h1>
            <p className="text-slate-500">Visão geral do seu negócio</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
            className="border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Clientes */}
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-[#E2E8F0]" onClick={() => navigate('/control-portal-clube/clients')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Clientes</span>
              <div className="p-2 bg-[#2563EB]/10 rounded-lg">
                <Users className="w-4 h-4 text-[#2563EB]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#0B1F3A]">{kpis.clients?.total || 0}</p>
            <div className="flex items-center gap-1 text-xs text-[#16A34A] mt-1">
              <TrendingUp className="w-3 h-3" />
              <span>+{kpis.clients?.new_this_week || 0} esta semana</span>
            </div>
          </Card>

          {/* Prestadores */}
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-[#E2E8F0]" onClick={() => navigate('/control-portal-clube/providers')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Prestadores</span>
              <div className="p-2 bg-[#16A34A]/10 rounded-lg">
                <UserCheck className="w-4 h-4 text-[#16A34A]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#0B1F3A]">{kpis.providers?.total || 0}</p>
            <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
              <TrendingUp className="w-3 h-3" />
              <span>+{kpis.providers?.new_this_week || 0} esta semana</span>
            </div>
          </Card>

          {/* Agendamentos Hoje */}
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/control-portal-clube/appointments')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Agendamentos Hoje</span>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-4 h-4 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold">{kpis.appointments?.today || 0}</p>
            <p className="text-xs text-slate-500 mt-1">{kpis.appointments?.total || 0} total</p>
          </Card>

          {/* Receita Mensal */}
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/control-portal-clube/financial')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Receita do Mês</span>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Wallet className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(kpis.revenue?.monthly)}</p>
            <div className={`flex items-center gap-1 text-xs mt-1 ${kpis.revenue?.growth_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {kpis.revenue?.growth_percent >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span>{kpis.revenue?.growth_percent >= 0 ? '+' : ''}{kpis.revenue?.growth_percent || 0}% vs mês anterior</span>
            </div>
          </Card>

          {/* Avaliações */}
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/control-portal-clube/reviews')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Avaliação Média</span>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Star className="w-4 h-4 text-amber-600" />
              </div>
            </div>
            <div className="flex items-baseline gap-1">
              <p className="text-2xl font-bold">{kpis.reviews?.average_rating || 0}</p>
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            </div>
            <p className="text-xs text-slate-500 mt-1">{kpis.reviews?.total || 0} avaliações</p>
          </Card>
        </div>

        {/* Alertas de Atenção */}
        {alerts.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Atenção Hoje
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {alerts.map((alert, idx) => {
                const Icon = ALERT_ICONS[alert.icon] || AlertTriangle;
                return (
                  <Card 
                    key={idx}
                    data-testid={`alert-card-${alert.icon}`}
                    className={`p-4 border cursor-pointer hover:shadow-md transition-shadow ${ALERT_COLORS[alert.type] || ALERT_COLORS.info}`}
                    onClick={() => alert.action && navigate(resolveAdminPath(alert.action))}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{alert.title}</span>
                      <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Agendamentos por Status */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Agendamentos por Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-700">{appointmentStatus.pending || 0}</p>
              <p className="text-xs text-yellow-600">Pendentes</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{appointmentStatus.accepted || 0}</p>
              <p className="text-xs text-blue-600">Aceitos</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Car className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{appointmentStatus.in_progress || 0}</p>
              <p className="text-xs text-purple-600">Em Andamento</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">{appointmentStatus.completed || 0}</p>
              <p className="text-xs text-green-600">Concluídos</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-700">{appointmentStatus.cancelled || 0}</p>
              <p className="text-xs text-red-600">Cancelados</p>
            </div>
          </div>
        </Card>

        {/* Dados Recentes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Últimos Clientes */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Últimos Clientes</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/control-portal-clube/clients')}>
                Ver todos <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {recent.clients?.length > 0 ? (
              <div className="space-y-3">
                {recent.clients.slice(0, 5).map((client, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => navigate(`/control-portal-clube/clients/${client.id}`)}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {getInitials(client.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{client.name}</p>
                      <p className="text-xs text-slate-500 truncate">{client.email}</p>
                    </div>
                    <Badge className={STATUS_COLORS[client.status] || STATUS_COLORS.ATIVO}>
                      {client.status || 'ATIVO'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum cliente cadastrado</p>
              </div>
            )}
          </Card>

          {/* Últimos Prestadores */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Últimos Prestadores</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/control-portal-clube/providers')}>
                Ver todos <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {recent.providers?.length > 0 ? (
              <div className="space-y-3">
                {recent.providers.slice(0, 5).map((provider, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => navigate(`/control-portal-clube/providers/${provider.id}`)}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-green-100 text-green-700">
                        {getInitials(provider.business_name || provider.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{provider.business_name || provider.name}</p>
                      <p className="text-xs text-slate-500 truncate">{provider.email}</p>
                    </div>
                    {provider.blocked ? (
                      <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <UserCheck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum prestador cadastrado</p>
              </div>
            )}
          </Card>

          {/* Últimos Agendamentos */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Últimos Agendamentos</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/control-portal-clube/appointments')}>
                Ver todos <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {recent.appointments?.length > 0 ? (
              <div className="space-y-3">
                {recent.appointments.slice(0, 5).map((appt, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{appt.client_name || 'Cliente'}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {appt.provider_name || 'Prestador'} • {appt.date || 'N/A'}
                      </p>
                    </div>
                    <Badge className={STATUS_COLORS[appt.status] || 'bg-gray-100 text-gray-700'}>
                      {appt.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum agendamento</p>
              </div>
            )}
          </Card>

          {/* Últimas Avaliações */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Últimas Avaliações</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/control-portal-clube/reviews')}>
                Ver todas <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {recent.reviews?.length > 0 ? (
              <div className="space-y-3">
                {recent.reviews.slice(0, 5).map((review, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < (review.rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}`} 
                        />
                      ))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{review.client_name || 'Cliente'}</p>
                      <p className="text-xs text-slate-500 truncate">{review.provider_name || 'Prestador'}</p>
                    </div>
                    <span className="text-xs text-slate-400">{formatDate(review.created_at)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Star className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma avaliação</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
